import { NextResponse } from "next/server";

/**
 * One error shape for the whole public API.
 *
 * Clients parse `code`, humans read `message`. Both are always present, so an
 * integrator never has to string-match on prose that might be reworded later.
 */

export type ApiErrorCode =
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "invalid_request"
  | "unsupported_media_type"
  | "payload_too_large"
  | "insufficient_credits"
  | "rate_limited"
  | "conflict"
  | "gone"
  | "server_error"
  | "service_unavailable";

const STATUS: Record<ApiErrorCode, number> = {
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  invalid_request: 400,
  unsupported_media_type: 415,
  payload_too_large: 413,
  insufficient_credits: 402,
  rate_limited: 429,
  conflict: 409,
  // A route that has been retired for good, not one that is missing or down.
  gone: 410,
  server_error: 500,
  service_unavailable: 503,
};

export interface ApiErrorOptions {
  /** Field-level detail for validation failures. */
  details?: unknown;
  headers?: Record<string, string>;
}

export function apiError(
  code: ApiErrorCode,
  message: string,
  options: ApiErrorOptions = {},
): NextResponse {
  return NextResponse.json(
    { error: { code, message, ...(options.details ? { details: options.details } : {}) } },
    { status: STATUS[code], headers: options.headers },
  );
}

/** Thrown inside handlers; converted by `withApi`. */
export class ApiException extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiException";
  }
}
